import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { Box, Dialog, IconButton, Typography, Button } from "@mui/material";

import { PaymentPopUpCloseIcon } from "../../../../assets/icons/MUI/PaymentIcons";
import { useCreatePaymentAccount, useUpdatePaymentAccount } from "../../../../api/queries/payment/paymentMutations";

import { ReceivingDetailsPopUpProps } from "../../payment.types";

import { getSchemaByStep } from "./schema";
import AllSetStep from "./Steps/AllSetStep";
import { Country, PaymentMethod } from "./types";
import CountryStepFields from "./Steps/CountryStepFields";
import PaymentMethodStepFields from "./Steps/PaymentMethodStepFields";
import WalletDetailsEgyptStepFields from "./Steps/WalletDetailsEgyptStepFields";
import WalletDetailsOtherStepFields from "./Steps/WalletDetailsOtherStepFields";
import BankAccountDetailsStepFields from "./Steps/BankAccountDetailsStepFields";

type ReceivingDetailsFormValues = {
  paymentMethod: PaymentMethod;
  country: "egypt" | "other" | null;
  walletType: string;
  phoneNumber: string;
  email: string;
  accountType: string;
  reEnterEmail: string;
  currency: string;
  accountHolderName: string;
  bankName: string;
  ibanNumber: string;
  bankCodeSwift: string;
  countryId: number | null;
};

const ReceivingDetailsPopUp = ({ onClose, open, user }: ReceivingDetailsPopUpProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
  const [country, setCountry] = useState<Country | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // const IS_LAST_STEP = (step === 2 && paymentMethod === "wallet") || (step === 1 && paymentMethod === "bankAccount");
  const IS_LAST_STEP = step === 2 && paymentMethod === "wallet";

  const schema = getSchemaByStep({
    step,
    country,
    paymentMethod,
  });

  const methods = useForm<ReceivingDetailsFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      // paymentMethod: "",
      paymentMethod: "wallet",
      country: null as "egypt" | "other" | null,
      walletType: "",
      phoneNumber: "",
      email: "",
      accountType: "",
      reEnterEmail: "",
      currency: "",
      accountHolderName: "",
      bankName: "",
      ibanNumber: "",
      bankCodeSwift: "",
      countryId: null as number | null,
    },
    mode: "onTouched",
  });

  useEffect(() => {
    if (user) {
      // const paymentMethodValue = user.type === "bank" ? "bankAccount" : "wallet";
      const paymentMethodValue = "wallet";
      const countryValue = user.eWalletType ? "egypt" : user.type === "e_wallet" ? "other" : undefined;

      setPaymentMethod(paymentMethodValue);
      setCountry(countryValue);

      // Reset form with user values
      methods.reset({
        paymentMethod: paymentMethodValue,
        country: countryValue || null,
        walletType: user.eWalletType || "",
        phoneNumber: user.phoneNumber || "",
        email: user.email || "",
        accountType: user.accountType || "",
        reEnterEmail: user.email || "",
        currency: user.currency || "",
        accountHolderName: user.fullName || "",
        bankName: user.bankName || "",
        ibanNumber: user.iban || "",
        bankCodeSwift: user.swift || "",
        countryId: countryValue === "egypt" ? 62 : null,
      });
    }
  }, [user]);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = methods;

  const handleClosePopUp = () => {
    methods.reset();
    setStep(0);
    setPaymentMethod(undefined);
    setCountry(undefined);
    onClose();
  };

  const { mutate: createPaymentAccount } = useCreatePaymentAccount(
    () => {
      handleClosePopUp();
      setIsSubmitting(false);
      setStep((prevStep) => prevStep + 1);
    },
    () => {
      setIsSubmitting(false);
    }
  );

  const { mutate: updatePaymentAccount } = useUpdatePaymentAccount(
    () => {
      handleClosePopUp();
      setIsSubmitting(false);
      setStep((prevStep) => prevStep + 1);
    },
    () => {
      setIsSubmitting(false);
    }
  );

  const onSubmit = (data: ReceivingDetailsFormValues) => {
    const allData = methods.getValues();

    if (step === 0) setPaymentMethod(data.paymentMethod);
    if (step === 1) {
      setCountry(data.country ?? undefined);
      // Set countryId when Egypt is selected
      if (data.country === "egypt") {
        methods.setValue("countryId", 62);
      } else {
        methods.setValue("countryId", null);
      }
    }

    if (!IS_LAST_STEP) {
      setStep((prevStep) => prevStep + 1);
    } else {
      setIsSubmitting(true);
      if (user?.id) {
        updatePaymentAccount({ payoutAccountId: user.id, body: allData });
      } else {
        createPaymentAccount(allData);
      }
    }
  };

  const submitButtonLabel = useMemo(() => {
    // if ((step === 2 && paymentMethod === "bankAccount") || (step === 3 && paymentMethod === "wallet")) {
    if (step === 3 && paymentMethod === "wallet") {
      return t("pages.payment.overview.v2.receivingDetailsPopUp.doneButton");
    }
    // if ((step === 1 && paymentMethod === "bankAccount") || (step === 2 && paymentMethod === "wallet")) {
    if (step === 2 && paymentMethod === "wallet") {
      return t("pages.payment.overview.v2.receivingDetailsPopUp.submitButton");
    }
    if (step === 0 || (step === 1 && paymentMethod === "wallet")) {
      return t("pages.payment.overview.v2.receivingDetailsPopUp.nextButton");
    }
    return "";
  }, [step, paymentMethod, t]);

  const headerLabel = useMemo(() => {
    if (step === 0) {
      return t("pages.payment.overview.v2.receivingDetailsPopUp.selectPaymentMethodTitle");
    }
    if (step === 1 && paymentMethod === "wallet") {
      return t("pages.payment.overview.v2.receivingDetailsPopUp.selectCountryLabel");
    }
    // if (step === 1 && paymentMethod === "bankAccount") {
    //   return t("pages.payment.overview.v2.receivingDetailsPopUp.bankAccountDetailsTitle");
    // }
    if (step === 2 && paymentMethod === "wallet") {
      return t("pages.payment.overview.v2.receivingDetailsPopUp.walletDetailsTitle");
    }

    if (IS_LAST_STEP) {
      return t("pages.payment.overview.v2.receivingDetailsPopUp.congratulations");
    }

    return "";
  }, [step, paymentMethod, t]);

  return (
    <Dialog
      onClose={handleClosePopUp}
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          width: "100%",
          maxWidth: "640px",
          borderRadius: "12px",
        },
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              position: "relative",
              padding: "24px 0",
              borderBottom: "1px solid #E4E7EC",
            }}
          >
            <Typography
              component="h3"
              sx={{
                color: "#101828",
                textAlign: "center",
                fontSize: "18px",
                fontWeight: 600,
                lineHeight: "28px",
                width: "100%",
              }}
            >
              {headerLabel}
            </Typography>
            <IconButton
              onClick={handleClosePopUp}
              sx={{
                position: "absolute",
                right: "16px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <PaymentPopUpCloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              padding: "20px 0 0",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {step === 0 && <PaymentMethodStepFields />}

            {step === 1 && paymentMethod === "wallet" && <CountryStepFields />}
            {step === 2 && paymentMethod === "wallet" && country === "egypt" && (
              <WalletDetailsEgyptStepFields
                errors={{
                  walletType: errors.walletType,
                  phoneNumber: errors.phoneNumber,
                  email: errors.email,
                }}
                register={{
                  walletType: register("walletType"),
                  email: register("email"),
                }}
                control={control}
              />
            )}
            {step === 2 && paymentMethod === "wallet" && country === "other" && (
              <WalletDetailsOtherStepFields
                errors={{
                  accountType: errors.accountType,
                  email: errors.email,
                  reEnterEmail: errors.reEnterEmail,
                  phoneNumber: errors.phoneNumber,
                  currency: errors.currency,
                }}
                register={{
                  accountType: register("accountType"),
                  email: register("email"),
                  reEnterEmail: register("reEnterEmail"),
                  phoneNumber: register("phoneNumber"),
                  currency: register("currency"),
                }}
              />
            )}
            {/* {step === 1 && paymentMethod === "bankAccount" && (
              <BankAccountDetailsStepFields
                errors={{
                  accountHolderName: errors.accountHolderName,
                  bankName: errors.bankName,
                  ibanNumber: errors.ibanNumber,
                  bankCodeSwift: errors.bankCodeSwift,
                  country: errors.country,
                  email: errors.email,
                }}
                register={{
                  accountHolderName: register("accountHolderName"),
                  bankName: register("bankName"),
                  ibanNumber: register("ibanNumber"),
                  bankCodeSwift: register("bankCodeSwift"),
                  country: register("country"),
                  email: register("email"),
                }}
              />
            )} */}
            {/* {((step === 2 && paymentMethod === "bankAccount") || (step === 3 && paymentMethod === "wallet")) && ( */}
            {step === 3 && paymentMethod === "wallet" && <AllSetStep />}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                padding: "12px 24px",
                borderTop: "1px solid var(--Gray-200, #EAECF0)",
              }}
            >
              {step > 0 && !IS_LAST_STEP && (
                <Button onClick={handleClosePopUp} variant="outlined">
                  {t("pages.payment.overview.v2.receivingDetailsPopUp.cancelButton")}
                </Button>
              )}
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {submitButtonLabel}
              </Button>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default ReceivingDetailsPopUp;
